// Provider-lead capture for the /list-your-storage journey.
//
// INTERIM STORAGE (no backend access yet): leads are POSTed to a Google Sheet
// through a Google Apps Script web app. Setup steps + the script to paste are
// in LEAD_TRACKING_SETUP.md at the repo root. Paste the deployed web-app URL
// below and leads will start landing in the sheet.
//
// Every lead is also mirrored to localStorage("providerLeads") so nothing is
// lost if the sheet call fails or the URL is not configured yet.
//
// When the real backend is ready, replace the fetch below with:
// import axios from "axios";
// import { API_URL } from "./userActions";
// const { data } = await axios.post(`${API_URL}/providers/lead/`, payload);

const GOOGLE_SHEET_WEBAPP_URL =
  "https://script.google.com/macros/s/AKfycbwSmcdeNlVJ5AvtFDM4UK9APyTBQrnLjWeLjeKQ9OsTeI80yyc9QjKJQn92lkEy-MA-Cw/exec";

const LEADS_STORAGE_KEY = "providerLeads";

// Stable key for one person's trip through the wizard. The early email capture
// and the final submission are sent with the same lead_id and a `stage` marker,
// so the sheet can update the existing row instead of ending up with an
// email-only record and a complete record for the same person.
export const createLeadId = () =>
  `lead_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

// Local mirror — inspect via DevTools > Application > Local Storage
const mirrorLocally = (payload) => {
  const stored = JSON.parse(localStorage.getItem(LEADS_STORAGE_KEY) || "[]");
  stored.push(payload);
  localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(stored));
};

// Early email capture (Step 2), sent the moment that step validates rather than
// waiting for the final submit. This one deliberately does NOT use `no-cors`:
// the wizard holds the user on Step 2 when the save fails, and an opaque
// response would make a 403 or a 500 look exactly like a success. `text/plain`
// keeps it a CORS-simple request, so there is still no preflight for Apps
// Script to answer.
export const saveProviderLeadEmail = async ({ email, lead_id, utm }) => {
  const payload = {
    lead_id,
    email,
    stage: "email_capture",
    utm,
    submitted_at: new Date().toISOString(),
  };

  mirrorLocally(payload);

  if (!GOOGLE_SHEET_WEBAPP_URL) {
    console.warn("[leads] GOOGLE_SHEET_WEBAPP_URL not set — email saved to localStorage only");
    return { success: true, lead: payload };
  }

  const response = await fetch(GOOGLE_SHEET_WEBAPP_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Lead email save failed (${response.status})`);
  }

  return { success: true, lead: payload };
};

export const submitProviderLead = async (lead) => {
  const payload = {
    ...lead,
    submitted_at: new Date().toISOString(),
  };

  mirrorLocally(payload);

  if (GOOGLE_SHEET_WEBAPP_URL) {
    // no-cors + text/plain avoids a CORS preflight, which Apps Script cannot
    // answer. The response is opaque (unreadable), but the write goes through;
    // a network failure still throws and is handled by the caller.
    await fetch(GOOGLE_SHEET_WEBAPP_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
    });
  } else {
    console.warn("[leads] GOOGLE_SHEET_WEBAPP_URL not set — lead saved to localStorage only");
  }

  return { success: true, lead: payload };
};
